import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import {
  ArrowLeft, RefreshCw, Users, Building2, Calendar, FileText,
  TrendingUp, Activity, Clock, BarChart3, PieChart, Loader2,
  ChevronDown, Download
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Category colors for charts
const categoryColors = {
  auth: '#3B82F6',
  user: '#10B981',
  horse: '#F59E0B',
  course: '#8B5CF6',
  billing: '#EF4444',
  group: '#06B6D4',
  settings: '#6B7280',
  admin: '#EC4899',
};

export function SuperAdminStats() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [days, setDays] = useState('30');
  
  // Data states
  const [overview, setOverview] = useState(null);
  const [loginsByDay, setLoginsByDay] = useState([]);
  const [actionsByCategory, setActionsByCategory] = useState([]);
  const [topActions, setTopActions] = useState([]);
  const [activityByClub, setActivityByClub] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [hourlyDistribution, setHourlyDistribution] = useState([]);

  const fetchAllStats = useCallback(async () => {
    try {
      const [
        overviewRes,
        loginsRes,
        categoriesRes,
        actionsRes,
        clubsRes,
        recentRes,
        hourlyRes
      ] = await Promise.all([
        axios.get(`${API_URL}/api/superadmin/site-stats/overview`),
        axios.get(`${API_URL}/api/superadmin/site-stats/logins-by-day?days=${days}`),
        axios.get(`${API_URL}/api/superadmin/site-stats/actions-by-category?days=${days}`),
        axios.get(`${API_URL}/api/superadmin/site-stats/top-actions?days=${days}`),
        axios.get(`${API_URL}/api/superadmin/site-stats/activity-by-club?days=${days}`),
        axios.get(`${API_URL}/api/superadmin/site-stats/recent-activity`),
        axios.get(`${API_URL}/api/superadmin/site-stats/hourly-distribution?days=${days}`)
      ]);

      setOverview(overviewRes.data);
      setLoginsByDay(loginsRes.data.logins_by_day || []);
      setActionsByCategory(categoriesRes.data.actions_by_category || []);
      setTopActions(actionsRes.data.top_actions || []);
      setActivityByClub(clubsRes.data.activity_by_club || []);
      setRecentActivity(recentRes.data.recent_activity || []);
      setHourlyDistribution(hourlyRes.data.hourly_distribution || []);
    } catch (error) {
      console.error('Error fetching stats:', error);
      toast.error('Erreur lors du chargement des statistiques');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [days]);

  useEffect(() => {
    fetchAllStats();
  }, [fetchAllStats]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchAllStats();
  };

  const handleDaysChange = (value) => {
    setDays(value);
    setLoading(true);
  };

  // Get max value for bar chart scaling
  const maxLoginCount = Math.max(...loginsByDay.map(d => d.total_logins), 1);
  const maxHourlyCount = Math.max(...hourlyDistribution.map(h => h.count), 1);
  const maxCategoryCount = Math.max(...actionsByCategory.map(c => c.count), 1);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin text-[#2D5A3D] mx-auto mb-4" />
          <p className="text-stone-600">Chargement des statistiques...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50" data-testid="superadmin-stats-page">
      {/* Header */}
      <header className="bg-white border-b border-stone-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/superadmin')}
                className="text-stone-600"
                data-testid="back-btn"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Retour
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-stone-800 flex items-center gap-2">
                  <BarChart3 className="w-6 h-6 text-[#2D5A3D]" />
                  Statistiques du site
                </h1>
                <p className="text-sm text-stone-500">Vue d'ensemble de l'activité</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Select value={days} onValueChange={handleDaysChange}>
                <SelectTrigger className="w-40" data-testid="period-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">7 derniers jours</SelectItem>
                  <SelectItem value="30">30 derniers jours</SelectItem>
                  <SelectItem value="60">60 derniers jours</SelectItem>
                  <SelectItem value="90">90 derniers jours</SelectItem>
                </SelectContent>
              </Select>
              <Button
                variant="outline"
                size="sm"
                onClick={handleRefresh}
                disabled={refreshing}
                data-testid="refresh-btn"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                Actualiser
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Overview Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardDescription className="flex items-center gap-1">
                <Building2 className="w-4 h-4" />
                Clubs actifs
              </CardDescription>
              <CardTitle className="text-3xl text-[#2D5A3D]" data-testid="active-clubs-count">
                {overview?.active_clubs || 0}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-stone-500">sur {overview?.total_clubs || 0} total</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardDescription className="flex items-center gap-1">
                <Users className="w-4 h-4" />
                Utilisateurs
              </CardDescription>
              <CardTitle className="text-3xl" data-testid="total-users-count">
                {overview?.total_users || 0}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-stone-500">
                <span className="text-green-600 font-medium">{overview?.active_users_7d || 0}</span> actifs (7j)
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardDescription className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                Cours créés
              </CardDescription>
              <CardTitle className="text-3xl" data-testid="total-courses-count">
                {overview?.total_courses || 0}
              </CardTitle>
            </CardHeader>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardDescription className="flex items-center gap-1">
                <FileText className="w-4 h-4" />
                Factures
              </CardDescription>
              <CardTitle className="text-3xl" data-testid="total-invoices-count">
                {overview?.total_invoices || 0}
              </CardTitle>
            </CardHeader>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardDescription className="flex items-center gap-1">
                <Activity className="w-4 h-4" />
                Actions loguées
              </CardDescription>
              <CardTitle className="text-3xl text-blue-600" data-testid="total-logs-count">
                {overview?.total_activity_logs?.toLocaleString() || 0}
              </CardTitle>
            </CardHeader>
          </Card>
        </div>

        {/* Login Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-[#2D5A3D]" />
              Connexions par jour
            </CardTitle>
            <CardDescription>
              {days} derniers jours - Total: {loginsByDay.reduce((sum, d) => sum + d.total_logins, 0)} connexions
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loginsByDay.length === 0 ? (
              <p className="text-center text-stone-500 py-8">Aucune donnée disponible</p>
            ) : (
              <div className="space-y-2">
                {/* Simple bar chart */}
                <div className="flex items-end gap-1 h-40 overflow-x-auto pb-2">
                  {loginsByDay.map((day, idx) => (
                    <div key={idx} className="flex flex-col items-center min-w-[24px]" title={`${day.date}: ${day.total_logins} connexions (${day.unique_users} utilisateurs)`}>
                      <div
                        className="w-5 bg-[#2D5A3D] rounded-t transition-all hover:bg-[#3D7A5D]"
                        style={{ height: `${(day.total_logins / maxLoginCount) * 100}%`, minHeight: day.total_logins > 0 ? '4px' : '0' }}
                      />
                      <span className="text-[9px] text-stone-400 mt-1 rotate-[-45deg] origin-top-left whitespace-nowrap">
                        {format(new Date(day.date), 'dd/MM', { locale: fr })}
                      </span>
                    </div>
                  ))}
                </div>
                <div className="flex justify-between text-xs text-stone-500 pt-4">
                  <span>Début période</span>
                  <span>Aujourd'hui</span>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Two column layout */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Actions by Category */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <PieChart className="w-5 h-5 text-purple-600" />
                Actions par catégorie
              </CardTitle>
            </CardHeader>
            <CardContent>
              {actionsByCategory.length === 0 ? (
                <p className="text-center text-stone-500 py-8">Aucune donnée disponible</p>
              ) : (
                <div className="space-y-3">
                  {actionsByCategory.map((cat, idx) => (
                    <div key={idx} className="flex items-center gap-3">
                      <div 
                        className="w-3 h-3 rounded-full flex-shrink-0"
                        style={{ backgroundColor: categoryColors[cat.category] || '#6B7280' }}
                      />
                      <span className="text-sm text-stone-700 flex-grow">{cat.label}</span>
                      <div className="flex-grow max-w-[200px]">
                        <div className="h-2 bg-stone-100 rounded-full overflow-hidden">
                          <div
                            className="h-full rounded-full transition-all"
                            style={{ 
                              width: `${(cat.count / maxCategoryCount) * 100}%`,
                              backgroundColor: categoryColors[cat.category] || '#6B7280'
                            }}
                          />
                        </div>
                      </div>
                      <span className="text-sm font-medium text-stone-600 w-16 text-right">
                        {cat.count.toLocaleString()}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Top Actions */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Activity className="w-5 h-5 text-blue-600" />
                Actions les plus fréquentes
              </CardTitle>
            </CardHeader>
            <CardContent>
              {topActions.length === 0 ? (
                <p className="text-center text-stone-500 py-8">Aucune donnée disponible</p>
              ) : (
                <div className="space-y-2">
                  {topActions.slice(0, 10).map((action, idx) => (
                    <div key={idx} className="flex items-center justify-between py-1 border-b border-stone-100 last:border-0">
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-stone-400 w-5">{idx + 1}.</span>
                        <span className="text-sm text-stone-700">{action.label}</span>
                      </div>
                      <Badge variant="secondary" className="text-xs">
                        {action.count.toLocaleString()}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Activity by Club */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-amber-600" />
              Activité par club
            </CardTitle>
            <CardDescription>Top 10 des clubs les plus actifs</CardDescription>
          </CardHeader>
          <CardContent>
            {activityByClub.length === 0 ? (
              <p className="text-center text-stone-500 py-8">Aucune donnée disponible</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-stone-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-stone-500 uppercase">#</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-stone-500 uppercase">Club</th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-stone-500 uppercase">Actions</th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-stone-500 uppercase">Connexions</th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-stone-500 uppercase">Utilisateurs actifs</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-stone-100">
                    {activityByClub.map((club, idx) => (
                      <tr key={idx} className="hover:bg-stone-50">
                        <td className="px-4 py-3 text-sm text-stone-400">{idx + 1}</td>
                        <td className="px-4 py-3 text-sm font-medium text-stone-800">{club.club_name || 'Club inconnu'}</td>
                        <td className="px-4 py-3 text-sm text-right text-stone-600">{club.action_count.toLocaleString()}</td>
                        <td className="px-4 py-3 text-sm text-right text-stone-600">{club.login_count.toLocaleString()}</td>
                        <td className="px-4 py-3 text-sm text-right">
                          <Badge variant="outline" className="text-[#2D5A3D]">{club.unique_users}</Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Hourly Distribution */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-cyan-600" />
              Distribution horaire de l'activité
            </CardTitle>
            <CardDescription>Heures d'utilisation du site (UTC)</CardDescription>
          </CardHeader>
          <CardContent>
            {hourlyDistribution.length === 0 ? (
              <p className="text-center text-stone-500 py-8">Aucune donnée disponible</p>
            ) : (
              <div className="flex items-end gap-1 h-32">
                {Array.from({ length: 24 }, (_, i) => {
                  const hourData = hourlyDistribution.find(h => h.hour === i);
                  const count = hourData?.count || 0;
                  return (
                    <div
                      key={i}
                      className="flex-1 flex flex-col items-center"
                      title={`${i}h: ${count} actions`}
                    >
                      <div
                        className="w-full bg-cyan-500 rounded-t transition-all hover:bg-cyan-600"
                        style={{ 
                          height: `${(count / maxHourlyCount) * 100}%`,
                          minHeight: count > 0 ? '2px' : '0'
                        }}
                      />
                      <span className="text-[9px] text-stone-400 mt-1">
                        {i.toString().padStart(2, '0')}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Activity */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Activity className="w-5 h-5 text-green-600" />
              Activité récente
            </CardTitle>
            <CardDescription>20 dernières actions sur le site</CardDescription>
          </CardHeader>
          <CardContent>
            {recentActivity.length === 0 ? (
              <p className="text-center text-stone-500 py-8">Aucune activité récente</p>
            ) : (
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {recentActivity.map((log, idx) => (
                  <div key={idx} className="flex items-center gap-3 py-2 border-b border-stone-100 last:border-0">
                    <div 
                      className="w-2 h-2 rounded-full flex-shrink-0"
                      style={{ backgroundColor: categoryColors[log.category] || '#6B7280' }}
                    />
                    <div className="flex-grow min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium text-stone-800 truncate">
                          {log.user_email}
                        </span>
                        <Badge variant="secondary" className="text-xs">
                          {log.action_label}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-stone-500">
                        <span>{log.club_name || 'Sans club'}</span>
                        {log.target_name && (
                          <>
                            <span>•</span>
                            <span className="truncate">{log.target_name}</span>
                          </>
                        )}
                      </div>
                    </div>
                    <span className="text-xs text-stone-400 whitespace-nowrap flex-shrink-0">
                      {log.timestamp ? format(new Date(log.timestamp), 'dd/MM HH:mm', { locale: fr }) : '-'}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}

export default SuperAdminStats;
